import type { GraphQLResult } from '@aws-amplify/api-graphql';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { dateUtils } from 'src/utils/dateUtils';
import { logger } from '../../../utils/logger';
import { isMetricsInitialized, trackApiCall } from '../../../utils/metrics';
import { logVtlMutationError } from '../auditLogger';

// Module-level variables to store the client instance and cached userAlias
let _graphQLClient: ReturnType<typeof generateClient> | null = null;
let _cachedUserAlias: string | null = null;

/**
 * Initialize and cache the user alias from the current auth session
 * This is called once when the GraphQL client is first created
 * Uses the same extraction logic as extractUserInfo in authUtils.ts
 */
const initializeUserAlias = async (): Promise<string | null> => {
  if (_cachedUserAlias) {
    return _cachedUserAlias;
  }

  try {
    const authUser = await getCurrentUser();
    const session = await fetchAuthSession();
    const payload = session.tokens?.idToken?.payload ?? {};

    // Extract userAlias from identities array (same logic as extractUserInfo)
    const identities = payload.identities as
      | Array<{ userId?: string; providerName?: string; providerType?: string; primary?: string }>
      | undefined;

    const userAlias =
      identities && identities.length > 0 && typeof identities[0].userId === 'string'
        ? identities[0].userId
        : (authUser.username?.replace('AmazonFederate_', '') ?? '');

    _cachedUserAlias = userAlias;
    return userAlias;
  } catch (error) {
    logger.error('Failed to initialize user alias for GraphQL headers', { error });
    return null;
  }
};

/**
 * Reset the cached user alias (useful for testing or when user changes)
 */
export const resetGraphQLUserAlias = (): void => {
  _cachedUserAlias = null;
};

/**
 * Get GraphQL client using singleton pattern with custom headers
 * This ensures we only create one client instance throughout the app
 * Custom headers including X-Verity-UserAlias are added to all requests
 * UserAlias is cached to avoid repeated fetchAuthSession calls
 */
const getGraphQLClient = (): ReturnType<typeof generateClient> => {
  if (!_graphQLClient) {
    _graphQLClient = generateClient({
      headers: async () => {
        const headers: Record<string, string> = {};

        // Get cached userAlias or initialize it once
        const userAlias = await initializeUserAlias();

        if (userAlias) {
          headers['x-verity-user-alias'] = userAlias;
        }

        return headers;
      },
    });
  }
  return _graphQLClient;
};

/**
 * Simple interface for GraphQL request options
 */
interface GraphQLRequestOptions {
  query: string;
  variables?: Record<string, unknown>;
  requestName: string;
  requestType: 'query' | 'mutation' | 'subscription';
  /** Optional audit config for VTL mutation error logging. When provided, errors are
   *  automatically logged to dim_audit_log via a separate createAuditLogEntry mutation. */
  auditConfig?: {
    /** Identifier for the page/operation, matching stored procedure audit entries */
    pageName: string;
    /** Type of operation — defaults to 'crud'. Use 'bulk_edit' for bulk operations. */
    operationType?: string;
  };
}

/**
 * Interface for GraphQL error info structure
 */
interface GraphQLErrorInfo {
  statusCode?: number;
  errorCode?: string;
  errorMessage?: string;
  requestId?: string;
}

/**
 * Interface for GraphQL error structure
 */
interface GraphQLError {
  message: string;
  path?: string[];
  errorType?: string;
  errorInfo?: GraphQLErrorInfo;
  locations?: Array<{
    line: number;
    column: number;
    sourceName?: string | null;
  }>;
}

/**
 * Extract meaningful error message from GraphQL errors
 * Prioritizes errorInfo.errorMessage over the generic message
 */
const extractErrorMessage = (errors: GraphQLError[]): string => {
  if (!errors || errors.length === 0) {
    return 'Unknown GraphQL error';
  }

  const firstError = errors[0];

  // Check if we have detailed error info
  if (firstError.errorInfo?.errorMessage) {
    return firstError.errorInfo.errorMessage;
  }

  // Fall back to the generic message
  if (firstError.message) {
    return firstError.message;
  }

  // Last resort
  return 'Unknown GraphQL error';
};

/**
 * Extract additional error context for logging
 */
const extractErrorContext = (errors: GraphQLError[]) => {
  if (!errors || errors.length === 0) {
    return {};
  }

  const firstError = errors[0];
  return {
    errorType: firstError.errorType,
    errorCode: firstError.errorInfo?.errorCode,
    statusCode: firstError.errorInfo?.statusCode,
    requestId: firstError.errorInfo?.requestId,
    path: firstError.path,
  };
};

/**
 * Execute a GraphQL request with enhanced error handling and logging
 * This is a wrapper around Amplify's GraphQL client that properly handles GraphQL errors
 *
 * @param options - GraphQL request options
 * @returns Promise resolving to typed response data
 */
export const fetchGraphQLRequest = async <T>({
  query,
  variables = {},
  requestName,
  requestType,
  auditConfig,
}: GraphQLRequestOptions): Promise<T> => {
  const startTime: Date = dateUtils.now();

  try {
    // Get the GraphQL client instance (singleton)
    const client = getGraphQLClient();

    // Execute the GraphQL operation
    const response = await client.graphql({
      query,
      variables,
    });

    // Calculate response time for logging
    const elapsed = Date.now() - startTime.getTime();

    // Type assertion to access the response properties
    const result = response as GraphQLResult<T>;

    // Check for errors in the response
    if (result.errors && result.errors.length > 0) {
      // Extract meaningful error message
      const errorMessage = extractErrorMessage(result.errors as GraphQLError[]);
      const errorContext = extractErrorContext(result.errors as GraphQLError[]);

      // Log the error with detailed information
      logger.error(`Request Name: ${requestName}`, {
        apiEvent: {
          operationName: requestName,
          operationType: requestType,
          requestStatus: 'API_FAILED',
          responseTime: elapsed,
        },
        error: errorMessage,
        errorContext,
      });

      // Track failed API call metrics (only if metrics are initialized)
      if (isMetricsInitialized()) {
        trackApiCall(requestName, requestType.toUpperCase(), elapsed, false);
      }

      // Throw the error with the meaningful message
      throw new Error(errorMessage);
    }

    // Log successful API call
    logger.info(`Request Name: ${requestName}`, {
      apiEvent: {
        operationName: requestName,
        operationType: requestType,
        requestStatus: 'API_SUCCESS',
        responseTime: elapsed,
      },
    });

    // Track API call metrics (only if metrics are initialized)
    if (isMetricsInitialized()) {
      trackApiCall(requestName, requestType.toUpperCase(), elapsed, true);
    }

    // Return just the data portion of the response
    return result.data;
  } catch (error: unknown) {
    // Calculate response time for logging
    const elapsed = Date.now() - startTime.getTime();

    // Handle different types of errors
    let errorMessage = 'Unknown error occurred';
    let foundGraphQLErrors = false;

    if (error && typeof error === 'object') {
      // Check for various possible error structures
      const errorObj = error as Record<string, unknown> & {
        errors?: GraphQLError[];
        graphQLErrors?: GraphQLError[];
        message?: string;
      };

      // Check if it's a GraphQL response with errors
      if (errorObj.errors && Array.isArray(errorObj.errors)) {
        const extractedMessage = extractErrorMessage(errorObj.errors);
        const errorContext = extractErrorContext(errorObj.errors);
        foundGraphQLErrors = true;

        logger.error(`Request Name: ${requestName}`, {
          apiEvent: {
            operationName: requestName,
            operationType: requestType,
            requestStatus: 'API_FAILED',
            responseTime: elapsed,
          },
          error: extractedMessage,
          errorContext,
        });

        if (auditConfig && requestType === 'mutation') {
          // extract just the errorInfo portion of the response
          const auditErrorInfo = errorObj.errors[0]?.errorInfo ?? { errorMessage: extractedMessage };
          logVtlMutationError(auditConfig, auditErrorInfo, variables, _cachedUserAlias ?? '');
        }

        throw new Error(extractedMessage);
      }

      // Check if it's an Amplify error with nested GraphQL errors
      if (errorObj.graphQLErrors && Array.isArray(errorObj.graphQLErrors)) {
        const extractedMessage = extractErrorMessage(errorObj.graphQLErrors);
        const errorContext = extractErrorContext(errorObj.graphQLErrors);
        foundGraphQLErrors = true;

        logger.error(`Request Name: ${requestName}`, {
          apiEvent: {
            operationName: requestName,
            operationType: requestType,
            requestStatus: 'API_FAILED',
            responseTime: elapsed,
          },
          error: extractedMessage,
          errorContext,
        });

        if (auditConfig && requestType === 'mutation') {
          const auditErrorInfo = errorObj.graphQLErrors[0]?.errorInfo ?? { errorMessage: extractedMessage };
          logVtlMutationError(auditConfig, auditErrorInfo, variables, _cachedUserAlias ?? '');
        }

        throw new Error(extractedMessage);
      }

      // Check if it's an Error instance with a message
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      }
    }

    // Log the error with debugging info
    logger.error(`Request Name: ${requestName}`, {
      apiEvent: {
        operationName: requestName,
        operationType: requestType,
        requestStatus: 'API_FAILED',
        responseTime: elapsed,
      },
      error: errorMessage,
      foundGraphQLErrors,
    });

    // Track failed API call metrics (only if metrics are initialized)
    if (isMetricsInitialized()) {
      trackApiCall(requestName, requestType.toUpperCase(), elapsed, false);
    }

    if (auditConfig && requestType === 'mutation') {
      logVtlMutationError(auditConfig, { errorMessage }, variables, _cachedUserAlias ?? '');
    }

    // Re-throw the error for the service to handle
    throw new Error(errorMessage);
  }
};
